"use client";

/**
 * Card Grid Renderer
 * Renders a responsive grid of icon/emoji cards in both canvas (builder) and public view.
 */

import type React from "react";
import { useMemo } from "react";
import { CARD_GRID_MAX_DESKTOP_COLUMNS, COVER_CARD_GAP_PX, COVER_CARD_WIDTH_PX } from "../../../_logic/constants";
import { getBalancedRowSizes } from "../../../_logic/utils";
import type { CardGridComponent, CardGridItem } from "../../../_types";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { CardGridCardDisplay } from "./CardGridCardDisplay";
import { CardGridCoverDisplay } from "./CardGridCoverDisplay";
import { InlineTipTapEdit } from "./_shared/InlineTipTapEdit";
import { SectionContainer } from "./_shared/SectionContainer";
import { PROSE_SECTION_TEXT_VARS } from "./_shared/prose-section-text";

interface CardGridRendererProps {
  component: CardGridComponent;
}

interface CoverRow {
  cards: CardGridItem[];
  startIndex: number;
  maxWidth: number;
}

/**
 * Manual column overrides intentionally preserve the requested row width.
 * Auto layout uses `getBalancedRowSizes` instead to avoid orphaned final rows.
 */
function getFixedRowSizes(itemCount: number, columns: 2 | 3): number[] {
  const sizes: number[] = [];
  for (let remaining = itemCount; remaining > 0; remaining -= columns) {
    sizes.push(Math.min(columns, remaining));
  }
  return sizes;
}

function getCoverRowMaxWidth(rowSize: number): number {
  return rowSize * COVER_CARD_WIDTH_PX + Math.max(0, rowSize - 1) * COVER_CARD_GAP_PX;
}

function buildCoverRows(cards: CardGridItem[], rowSizes: number[]): CoverRow[] {
  let startIndex = 0;
  return rowSizes
    .map((rowSize) => {
      const rowCards = cards.slice(startIndex, startIndex + rowSize);
      const row: CoverRow = {
        cards: rowCards,
        startIndex,
        maxWidth: getCoverRowMaxWidth(rowCards.length),
      };
      startIndex += rowSize;
      return row;
    })
    .filter((row) => row.cards.length > 0);
}

function getStandardGridClass(itemCount: number, manualColumns: 2 | 3 | undefined, isPublic: boolean): string {
  if (itemCount <= 1) return "";

  const desktopColumns = manualColumns ?? (itemCount === 2 ? 2 : CARD_GRID_MAX_DESKTOP_COLUMNS);
  // itemCount === 2 always wins even with a manual 3-column override, so 2 cards never
  // leave an empty third grid slot.
  if (desktopColumns <= 2 || itemCount === 2) {
    return isPublic ? "sm:grid-cols-2" : "@sm:grid-cols-2";
  }

  return isPublic ? "sm:grid-cols-2 md:grid-cols-3" : "@sm:grid-cols-2 @md:grid-cols-3";
}

function getCoverRowClass(rowSize: number, isPublic: boolean): string {
  const baseClass = "mx-auto grid w-full grid-cols-1 justify-center gap-6";

  if (rowSize <= 1) return baseClass;

  const columnsClass =
    rowSize === 2 ? (isPublic ? "sm:grid-cols-2" : "@sm:grid-cols-2") : isPublic ? "sm:grid-cols-3" : "@sm:grid-cols-3";

  return `${baseClass} ${columnsClass}`;
}

/**
 * Renders a card grid section. Supports two layouts:
 * - "standard" (default): icon/emoji cards with optional link or button CTA
 * - "cover": full-bleed square tiles with background, centered title, and optional number/description
 */
export function CardGridRenderer({ component }: CardGridRendererProps): React.ReactElement {
  const { title, description, items = [] } = component;
  const layout = component.layout ?? "standard";
  const showNumbers = component.showNumbers ?? true;
  const descriptionDisplay = component.descriptionDisplay ?? "hover";
  const { isPublic } = usePublicViewContext();
  const isCover = layout === "cover";

  const coverRows = useMemo(
    () =>
      isCover
        ? buildCoverRows(
            items,
            component.columns
              ? getFixedRowSizes(items.length, component.columns)
              : getBalancedRowSizes(items.length, CARD_GRID_MAX_DESKTOP_COLUMNS)
          )
        : [],
    [isCover, items, component.columns]
  );
  const standardGridClass = useMemo(
    () => getStandardGridClass(items.length, component.columns, isPublic),
    [items.length, component.columns, isPublic]
  );
  const gridMaxWidthClass = items.length === 1 ? "max-w-sm" : items.length === 2 ? "max-w-2xl" : "";

  // Render
  return (
    <SectionContainer
      maxWidth="4xl"
      background={component.background}
      title={title || ""}
      componentId={component.id}
      titleFieldId="cardgrid-title"
    >
      <InlineTipTapEdit
        content={description || ""}
        className={`prose prose-base prose-gray max-w-none prose-a:text-[var(--lp-accent)] prose-a:underline ${PROSE_SECTION_TEXT_VARS}`}
        componentId={component.id}
        fieldId="cardgrid-description"
        field="description"
        placeholder="Click to add description..."
      />

      {items.length === 0 ? null : isCover ? (
        <div className="mx-auto flex w-full flex-col gap-6">
          {coverRows.map((row, rowIndex) => (
            <div
              key={`${component.id}-cover-row-${rowIndex}`}
              className={getCoverRowClass(row.cards.length, isPublic)}
              style={{ maxWidth: row.maxWidth }}
            >
              {row.cards.map((card, cardIndex) => (
                <CardGridCoverDisplay
                  key={card.id}
                  card={card}
                  componentId={component.id}
                  index={row.startIndex + cardIndex}
                  showNumbers={showNumbers}
                  descriptionDisplay={descriptionDisplay}
                />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className={`mx-auto grid grid-cols-1 ${standardGridClass} ${gridMaxWidthClass} gap-4`}>
          {items.map((card) => (
            <CardGridCardDisplay key={card.id} card={card} componentId={component.id} />
          ))}
        </div>
      )}
    </SectionContainer>
  );
}
